import { makeAutoObservable, runInAction } from 'mobx'
import {
	fetchPersonalResearchDefaults,
	fetchStudentAitoolsProfile,
	fetchStudentOnboardingProfile,
	fetchStudentResearchProfile,
	savePersonalResearchDefaults,
	saveStudentResearchProfile,
	listPersonalResearchJourneys,
	listStudentResearchJourneys,
	listStudentAitoolsJourneys,
	createPersonalResearchJourney,
	createStudentResearchJourney,
	patchPersonalResearchJourney,
	patchStudentResearchJourney,
	deletePersonalResearchJourney,
	deleteStudentResearchJourney,
} from 'api/research'
import { resolveResearchDefaults } from 'shared/utils/resolveResearchDefaults'
import { buildResumeHydrate } from 'pages/Research/utils/journeyResumeUtils'

const normalizeStudentId = id => {
	if (id == null || id === '') return null
	const trimmed = String(id).trim()
	return trimmed || null
}

const isAbortError = error =>
	Boolean(
		error &&
			(error.name === 'CanceledError' ||
				error.name === 'AbortError' ||
				error.code === 'ERR_CANCELED'),
	)

const copyDefaults = defaults => {
	if (!defaults) return null
	return {
		...defaults,
		countries: Array.isArray(defaults.countries) ? [...defaults.countries] : [],
	}
}

const areDefaultsEqual = (a, b) => {
	if (a === b) return true
	if (!a || !b) return false
	const keys = [
		'category',
		'subCategory',
		'selectedPlan',
		'career',
		'course',
		'highestLevel',
	]
	for (const key of keys) {
		if ((a[key] || '') !== (b[key] || '')) return false
	}
	const aCountries = Array.isArray(a.countries) ? a.countries : []
	const bCountries = Array.isArray(b.countries) ? b.countries : []
	if (aCountries.length !== bCountries.length) return false
	return aCountries.every((item, index) => item === bCountries[index])
}

const EMPTY_PROFILE = {
	category: '',
	subCategory: '',
	selectedPlan: '',
	career: '',
	course: '',
	countries: [],
	highestLevel: '',
}

class ResearchSessionStore {
	selectedStudentId = null
	sourcesGeneration = 0

	personalSource = null
	personalStatus = 'idle'
	personalError = null

	/** Counselor Student Profile (working state when student selected). */
	studentProfileSource = null
	/** @deprecated alias — same as studentProfileSource */
	get studentJourneySource() {
		return this.studentProfileSource
	}
	studentSource = null
	onboardingSource = null
	sourcesStatus = 'idle'
	sourcesError = null

	resolvedDefaults = null
	sourceByField = null
	baselineDefaults = null
	draftDefaults = null
	isDirty = false
	saveStatus = 'idle'
	saveError = null

	/** Session Journey reference for dual-write after CREATE/Resume. Never auto-resume. */
	activeJourneyId = null
	pendingMainCardJourney = false
	typeFlow = ''
	entryOrigin = null // 'main_card' | 'saved_resume' | 'stepper' | 'in_finder_nav' | 'student_journey_view'
	journeys = []
	journeysStatus = 'idle'
	journeysError = null
	journeySaveStatus = 'idle'

	/** Student-owned aitools_journeys — read-only for counselor UI. */
	studentAitoolsJourneys = []
	studentAitoolsJourneysStatus = 'idle'
	studentAitoolsJourneysError = null

	_studentAbortController = null
	_personalAbortController = null
	_personalGeneration = 0
	_personalInflight = null
	_persistGeneration = 0

	constructor() {
		makeAutoObservable(
			this,
			{
				_studentAbortController: false,
				_personalAbortController: false,
				_personalGeneration: false,
				_personalInflight: false,
				_persistGeneration: false,
			},
			{ autoBind: true },
		)
	}

	clearActiveJourneyReference() {
		this.activeJourneyId = null
	}

	beginMainCardJourney() {
		this.clearActiveJourneyReference()
		this.pendingMainCardJourney = true
		this.entryOrigin = 'main_card'
	}

	clearPendingMainCardJourney() {
		this.pendingMainCardJourney = false
	}

	getActiveJourney() {
		if (!this.activeJourneyId) return null
		return this.journeys.find(journey => journey.id === this.activeJourneyId) || null
	}

	setEntryOrigin(origin) {
		this.entryOrigin = origin || null
	}

	setTypeFlow(typeFlow) {
		this.typeFlow = typeFlow || ''
	}

	selectStudent(id, { force = false } = {}) {
		const nextId = normalizeStudentId(id)
		const sameId = nextId === this.selectedStudentId
		if (
			sameId &&
			nextId != null &&
			(this.sourcesStatus === 'loading' || this.sourcesStatus === 'success')
		) {
			return { ok: true, blocked: false }
		}
		if (sameId && nextId == null && this.personalStatus === 'success') {
			this._resolve()
			return { ok: true, blocked: false }
		}

		if (!sameId && this.isDirty && !force) {
			return { ok: false, blocked: true, reason: 'dirty' }
		}

		this._persistGeneration += 1
		this.saveStatus = 'idle'
		this.saveError = null

		const previousStudentId = this.selectedStudentId
		const personalSnapshot = copyDefaults(this.draftDefaults)

		this.selectedStudentId = nextId
		this.sourcesGeneration += 1
		const gen = this.sourcesGeneration
		this._abortStudentLoads()
		this._clearStudentSources()
		this.clearActiveJourneyReference()
		this.pendingMainCardJourney = false
		// After switch: first Career/Country/Course selection may CREATE; no auto Journey.
		this.entryOrigin = null
		this.typeFlow = this.typeFlow || ''

		if (nextId == null) {
			this.loadPersonal()
				.then(() => {
					if (gen !== this.sourcesGeneration) return
					if (this.selectedStudentId == null) {
						this._resolve()
						this.loadJourneys()
					}
				})
				.catch(() => {})
			return { ok: true, blocked: false }
		}

		// M6: switching TO a student copies Personal Profile → Counselor Student Profile.
		// Personal Profile / Journeys remain untouched. No Journey CREATE.
		const shouldCopyPersonal =
			previousStudentId == null &&
			personalSnapshot &&
			(personalSnapshot.category ||
				personalSnapshot.subCategory ||
				personalSnapshot.career ||
				personalSnapshot.course ||
				(personalSnapshot.countries || []).length > 0)

		this._loadStudentSources(nextId, gen, {
			copyFromPersonal: shouldCopyPersonal ? personalSnapshot : null,
		})
		return { ok: true, blocked: false }
	}

	ensureSessionReady() {
		if (this.selectedStudentId == null) {
			if (this.personalStatus === 'idle' || this.personalStatus === 'error') {
				return this.loadPersonal()
					.then(() => {
						if (this.selectedStudentId == null) {
							this._resolve()
							return this.loadJourneys()
						}
					})
					.catch(() => {})
			}
			if (this.personalStatus === 'success' && !this.resolvedDefaults) {
				this._resolve()
			}
			return this.loadJourneys()
		}

		if (this.sourcesStatus === 'success' || this.sourcesStatus === 'loading') {
			return this.loadJourneys()
		}

		this.sourcesGeneration += 1
		const gen = this.sourcesGeneration
		return this._loadStudentSources(this.selectedStudentId, gen).then(() =>
			this.loadJourneys(),
		)
	}

	reload() {
		if (this.selectedStudentId == null) {
			return this.loadPersonal({ force: true })
				.then(() => {
					if (this.selectedStudentId == null) {
						this._resolve()
						return this.loadJourneys({ force: true })
					}
				})
				.catch(() => {})
		}
		this.sourcesGeneration += 1
		const gen = this.sourcesGeneration
		this._abortStudentLoads()
		this._clearStudentSources()
		return this._loadStudentSources(this.selectedStudentId, gen).then(() =>
			this.loadJourneys({ force: true }),
		)
	}

	reset() {
		this.sourcesGeneration += 1
		this._personalGeneration += 1
		this._abortStudentLoads()
		this._abortPersonalLoad()
		this.selectedStudentId = null
		this.personalSource = null
		this.personalStatus = 'idle'
		this.personalError = null
		this._clearStudentSources()
		this.resolvedDefaults = null
		this.sourceByField = null
		this.baselineDefaults = null
		this.draftDefaults = null
		this.isDirty = false
		this.saveStatus = 'idle'
		this.saveError = null
		this.clearActiveJourneyReference()
		this.pendingMainCardJourney = false
		this.typeFlow = ''
		this.entryOrigin = null
		this.journeys = []
		this.journeysStatus = 'idle'
		this.journeysError = null
		this._clearStudentAitoolsJourneys()
	}

	loadPersonal({ force = false } = {}) {
		if (!force && this.personalStatus === 'success') {
			return Promise.resolve(this.personalSource)
		}
		if (!force && this.personalStatus === 'loading' && this._personalInflight) {
			return this._personalInflight
		}

		this._abortPersonalLoad()
		this._personalGeneration += 1
		const gen = this._personalGeneration
		const controller = new AbortController()
		this._personalAbortController = controller
		this.personalStatus = 'loading'
		this.personalError = null

		this._personalInflight = (async () => {
			try {
				const data = await fetchPersonalResearchDefaults({
					signal: controller.signal,
				})
				if (gen !== this._personalGeneration) return null
				runInAction(() => {
					this.personalSource = data
					this.personalStatus = 'success'
					this.personalError = null
				})
				return data
			} catch (error) {
				if (isAbortError(error) || gen !== this._personalGeneration) {
					return null
				}
				runInAction(() => {
					this.personalStatus = 'error'
					this.personalError =
						error?.response?.data?.error ||
						error?.message ||
						'Failed to load personal research defaults'
				})
				throw error
			} finally {
				if (gen === this._personalGeneration) {
					this._personalInflight = null
					this._personalAbortController = null
				}
			}
		})()

		return this._personalInflight
	}

	async loadJourneys({ force = false } = {}) {
		if (!force && this.journeysStatus === 'loading') return this.journeys
		this.journeysStatus = 'loading'
		this.journeysError = null
		try {
			const list =
				this.selectedStudentId == null
					? await listPersonalResearchJourneys({ limit: 5 })
					: await listStudentResearchJourneys(this.selectedStudentId, {
							limit: 5,
						})
			runInAction(() => {
				this.journeys = Array.isArray(list) ? list : []
				this.journeysStatus = 'success'
			})
			if (this.selectedStudentId == null) {
				this._clearStudentAitoolsJourneys()
			} else {
				await this.loadStudentAitoolsJourneys({ force: true })
			}
			return this.journeys
		} catch (error) {
			runInAction(() => {
				this.journeys = []
				this.journeysStatus = 'error'
				this.journeysError = error
			})
			if (this.selectedStudentId == null) {
				this._clearStudentAitoolsJourneys()
			} else {
				await this.loadStudentAitoolsJourneys({ force: true })
			}
			return []
		}
	}

	_clearStudentAitoolsJourneys() {
		this.studentAitoolsJourneys = []
		this.studentAitoolsJourneysStatus = 'idle'
		this.studentAitoolsJourneysError = null
	}

	async loadStudentAitoolsJourneys({ force = false } = {}) {
		const studentId = this.selectedStudentId
		if (studentId == null) {
			this._clearStudentAitoolsJourneys()
			return []
		}
		if (!force && this.studentAitoolsJourneysStatus === 'loading') {
			return this.studentAitoolsJourneys
		}
		this.studentAitoolsJourneysStatus = 'loading'
		this.studentAitoolsJourneysError = null
		try {
			const list = await listStudentAitoolsJourneys(studentId, { limit: 5 })
			runInAction(() => {
				if (this.selectedStudentId !== studentId) return
				this.studentAitoolsJourneys = Array.isArray(list) ? list : []
				this.studentAitoolsJourneysStatus = 'success'
			})
			return this.studentAitoolsJourneys
		} catch (error) {
			runInAction(() => {
				if (this.selectedStudentId !== studentId) return
				this.studentAitoolsJourneys = []
				this.studentAitoolsJourneysStatus = 'error'
				this.studentAitoolsJourneysError = error
			})
			return []
		}
	}

	/**
	 * View student aitools journey: hydrate counselor Profile once.
	 * Does not bind activeJourneyId — next selection CREATE counselor research-journey.
	 */
	async applyStudentJourneyAsDefaults(journey) {
		if (!journey) return { ok: false, reason: 'invalid_journey' }

		const countries = Array.isArray(journey.countries)
			? [...journey.countries]
			: typeof journey.countries === 'string' && journey.countries
				? [journey.countries]
				: []

		const hydrate = {
			category: journey.category || '',
			subCategory: journey.subCategory || '',
			selectedPlan: journey.selectedPlan || '',
			career: journey.career || '',
			course: journey.course || '',
			countries,
			highestLevel: journey.highestLevel || '',
		}

		const profileResult = await this.commitProfile(hydrate)
		if (!profileResult.ok) return profileResult

		runInAction(() => {
			this.clearActiveJourneyReference()
			this.pendingMainCardJourney = false
			this.typeFlow = journey.typeFlowJourney || this.typeFlow || ''
			this.entryOrigin = 'student_journey_view'
		})
		return { ok: true, journey }
	}

	_loadStudentSources(studentId, gen, { copyFromPersonal = null } = {}) {
		this._abortStudentLoads()
		const controller = new AbortController()
		this._studentAbortController = controller
		this.sourcesStatus = 'loading'
		this.sourcesError = null

		const { signal } = controller
		return (async () => {
			try {
				if (copyFromPersonal) {
					await saveStudentResearchProfile(studentId, {
						category: copyFromPersonal.category || null,
						subCategory: copyFromPersonal.subCategory || null,
						selectedPlan: copyFromPersonal.selectedPlan || null,
						career: copyFromPersonal.career || null,
						countries: Array.isArray(copyFromPersonal.countries)
							? copyFromPersonal.countries
							: [],
						course: copyFromPersonal.course || null,
						highestLevel: copyFromPersonal.highestLevel || null,
					})
				}

				const [profile, aitools, onboarding] = await Promise.all([
					fetchStudentResearchProfile(studentId, { signal }),
					fetchStudentAitoolsProfile(studentId, { signal }),
					fetchStudentOnboardingProfile(studentId, { signal }),
				])
				if (gen !== this.sourcesGeneration) return
				runInAction(() => {
					this.studentProfileSource = profile
					this.studentSource = aitools
					this.onboardingSource = onboarding
					this.sourcesStatus = 'success'
					this.sourcesError = null
					this._resolve()
				})
				await this.loadJourneys({ force: true })
			} catch (error) {
				if (isAbortError(error) || gen !== this.sourcesGeneration) return
				runInAction(() => {
					this.sourcesStatus = 'error'
					this.sourcesError =
						error?.response?.data?.error ||
						error?.message ||
						'Failed to load student research sources'
				})
			} finally {
				if (gen === this.sourcesGeneration) {
					this._studentAbortController = null
				}
			}
		})()
	}

	_resolve() {
		const { defaults, sourceByField } = resolveResearchDefaults({
			selectedStudentId: this.selectedStudentId,
			personalSource: this.personalSource,
			studentProfileSource: this.studentProfileSource,
			studentSource: this.studentSource,
			onboardingSource: this.onboardingSource,
		})
		this.resolvedDefaults = defaults
		this.sourceByField = sourceByField
		if (!this.isDirty) {
			this.baselineDefaults = copyDefaults(defaults)
			this.draftDefaults = copyDefaults(defaults)
		}
	}

	updateDraft(partial = {}) {
		const base =
			this.draftDefaults ||
			copyDefaults(this.resolvedDefaults) ||
			copyDefaults(EMPTY_PROFILE)

		const next = {
			...base,
			...partial,
			countries:
				partial.countries !== undefined
					? Array.isArray(partial.countries)
						? [...partial.countries]
						: []
					: Array.isArray(base.countries)
						? [...base.countries]
						: [],
		}

		this.draftDefaults = next
		this.isDirty = !areDefaultsEqual(next, this.baselineDefaults)
	}

	_draftToPersistPayload(draft) {
		const countries = Array.isArray(draft?.countries) ? [...draft.countries] : []
		return {
			category: draft?.category || null,
			subCategory: draft?.subCategory || null,
			selectedPlan: draft?.selectedPlan || null,
			career: draft?.career || null,
			countries,
			course: draft?.course || null,
			highestLevel: draft?.highestLevel || null,
		}
	}

	/**
	 * Persist working Profile only (SSOT). Does not create Journey.
	 */
	async commitProfile(partial = {}) {
		this.updateDraft(partial)
		const draft = this.draftDefaults
		if (!draft) return { ok: false, reason: 'no_draft' }

		const targetStudentId = this.selectedStudentId
		const gen = ++this._persistGeneration
		this.saveStatus = 'loading'
		this.saveError = null

		const payload = this._draftToPersistPayload(draft)

		try {
			const saved =
				targetStudentId == null
					? await savePersonalResearchDefaults(payload)
					: await saveStudentResearchProfile(targetStudentId, payload)

			if (gen !== this._persistGeneration) {
				return { ok: false, reason: 'stale' }
			}
			if (this.selectedStudentId !== targetStudentId) {
				return { ok: false, reason: 'student_changed' }
			}

			runInAction(() => {
				if (targetStudentId == null) {
					this.personalSource = saved
				} else {
					this.studentProfileSource = saved
				}
				this.baselineDefaults = copyDefaults(this.draftDefaults)
				this.isDirty = false
				this.saveStatus = 'success'
				this.saveError = null
				this._resolve()
			})
			return { ok: true, saved }
		} catch (error) {
			if (gen !== this._persistGeneration) {
				return { ok: false, reason: 'stale' }
			}
			runInAction(() => {
				this.saveStatus = 'error'
				this.saveError = error
			})
			return { ok: false, reason: 'error', error }
		}
	}

	/** @deprecated Use commitProfile — Profile SSOT writes only. */
	async commitDraft(partial = {}) {
		return this.commitProfile(partial)
	}

	_profileSnapshotForJourney() {
		const draft = this.draftDefaults || this.resolvedDefaults || EMPTY_PROFILE
		return {
			category: draft.category || null,
			subCategory: draft.subCategory || null,
			selectedPlan: draft.selectedPlan || null,
			career: draft.career || null,
			countries: Array.isArray(draft.countries) ? [...draft.countries] : [],
			course: draft.course || null,
			highestLevel: draft.highestLevel || null,
			typeFlowJourney: this.typeFlow || null,
		}
	}

	/**
	 * CREATE Journey from current Profile snapshot (history only).
	 */
	async createJourneyFromProfile(extra = {}) {
		const payload = {
			...this._profileSnapshotForJourney(),
			...extra,
		}
		if (!payload.typeFlowJourney || !payload.subCategory) {
			return { ok: false, reason: 'missing_required' }
		}

		this.journeySaveStatus = 'loading'
		try {
			const journey =
				this.selectedStudentId == null
					? await createPersonalResearchJourney(payload)
					: await createStudentResearchJourney(this.selectedStudentId, payload)

			runInAction(() => {
				this.activeJourneyId = journey?.id || null
				this.pendingMainCardJourney = false
				this.journeySaveStatus = 'success'
			})
			await this.loadJourneys({ force: true })
			return { ok: true, journey }
		} catch (error) {
			runInAction(() => {
				this.journeySaveStatus = 'error'
			})
			return { ok: false, reason: 'error', error }
		}
	}

	/**
	 * Dual-write: Profile already updated via commitProfile; PATCH active Journey.
	 */
	async patchActiveJourney(partial = {}) {
		if (!this.activeJourneyId) return { ok: false, reason: 'no_active_reference' }
		this.journeySaveStatus = 'loading'
		try {
			const journey =
				this.selectedStudentId == null
					? await patchPersonalResearchJourney(this.activeJourneyId, partial)
					: await patchStudentResearchJourney(
							this.selectedStudentId,
							this.activeJourneyId,
							partial,
						)
			runInAction(() => {
				this.journeySaveStatus = 'success'
			})
			await this.loadJourneys({ force: true })
			return { ok: true, journey }
		} catch (error) {
			runInAction(() => {
				this.journeySaveStatus = 'error'
			})
			return { ok: false, reason: 'error', error }
		}
	}

	/**
	 * Resume: load journey → hydrate Profile once → set session reference.
	 */
	async resumeJourney(journey) {
		if (!journey?.id) return { ok: false, reason: 'invalid_journey' }

		const resume = buildResumeHydrate(journey)
		const profileResult = await this.commitProfile({
			category: resume.category,
			subCategory: resume.subCategory,
			selectedPlan: resume.selectedPlan,
			career: resume.career,
			country: resume.country,
			countries: resume.countries,
			course: resume.course,
			highestLevel: resume.highestLevel,
		})
		if (!profileResult.ok) return profileResult

		runInAction(() => {
			this.activeJourneyId = journey.id
			this.pendingMainCardJourney = false
			this.typeFlow = journey.typeFlowJourney || this.typeFlow || ''
			this.entryOrigin = 'saved_resume'
		})
		return { ok: true, journey, resume }
	}

	async deleteJourney(journeyId) {
		if (!journeyId) return { ok: false }
		try {
			if (this.selectedStudentId == null) {
				await deletePersonalResearchJourney(journeyId)
			} else {
				await deleteStudentResearchJourney(this.selectedStudentId, journeyId)
			}
			if (this.activeJourneyId === journeyId) {
				this.clearActiveJourneyReference()
			}
			await this.loadJourneys({ force: true })
			return { ok: true }
		} catch (error) {
			return { ok: false, error }
		}
	}

	_sourcesSettled() {
		if (this.selectedStudentId == null) {
			return (
				this.personalStatus === 'success' || this.personalStatus === 'error'
			)
		}
		return (
			this.sourcesStatus === 'success' || this.sourcesStatus === 'error'
		)
	}

	_ensureEmptyDraftForSeed() {
		if (this.draftDefaults) return true
		if (!this._sourcesSettled()) return false
		const empty = copyDefaults(EMPTY_PROFILE)
		this.draftDefaults = empty
		this.baselineDefaults = copyDefaults(empty)
		this.isDirty = false
		return true
	}

	seedDraftIfEmpty({ category, subCategory } = {}) {
		if (!this._sourcesSettled()) return false
		if (!this._ensureEmptyDraftForSeed()) return false

		const needsCategory = !this.draftDefaults.category
		const needsSubCategory = !this.draftDefaults.subCategory
		if (!needsCategory && !needsSubCategory) return false

		if (
			this.isDirty &&
			(this.draftDefaults.category || this.draftDefaults.subCategory)
		) {
			return false
		}

		const nextCategory = needsCategory
			? category
			: this.draftDefaults.category
		const nextSubCategory = needsSubCategory
			? subCategory
			: this.draftDefaults.subCategory
		if (!nextCategory || !nextSubCategory) return false

		const next = {
			...this.draftDefaults,
			category: nextCategory,
			subCategory: nextSubCategory,
			countries: Array.isArray(this.draftDefaults.countries)
				? [...this.draftDefaults.countries]
				: [],
		}
		this.draftDefaults = next
		if (!this.isDirty) {
			this.baselineDefaults = copyDefaults(next)
		} else {
			this.baselineDefaults = copyDefaults(next)
			this.isDirty = false
		}
		return true
	}

	seedCareerIfEmpty({ career }) {
		if (!career) return false
		if (!this._sourcesSettled()) return false
		if (!this._ensureEmptyDraftForSeed()) return false
		if (this.draftDefaults.career) return false

		const careerLayer = this.sourceByField?.career
		if (
			careerLayer === 'counselorStudentProfile' ||
			careerLayer === 'studentJourney' ||
			careerLayer === 'student' ||
			careerLayer === 'personal'
		) {
			return false
		}

		const next = {
			...this.draftDefaults,
			career,
			countries: Array.isArray(this.draftDefaults.countries)
				? [...this.draftDefaults.countries]
				: [],
		}
		this.draftDefaults = next
		if (!this.isDirty) {
			this.baselineDefaults = copyDefaults(next)
		} else {
			this.isDirty = !areDefaultsEqual(next, this.baselineDefaults)
		}
		return true
	}

	seedCountryIfEmpty({ country }) {
		if (!country) return false
		if (!this._sourcesSettled()) return false
		if (!this._ensureEmptyDraftForSeed()) return false

		const existingCountries = Array.isArray(this.draftDefaults.countries)
			? this.draftDefaults.countries
			: []
		if (existingCountries.length > 0) return false
		if (this.draftDefaults.country) return false

		const countryLayer = this.sourceByField?.countries
		if (
			countryLayer === 'counselorStudentProfile' ||
			countryLayer === 'studentJourney' ||
			countryLayer === 'student'
		) {
			return false
		}

		if (
			this.isDirty &&
			(existingCountries.length > 0 || this.draftDefaults.country)
		) {
			return false
		}

		const next = {
			...this.draftDefaults,
			country,
		}
		this.draftDefaults = next
		if (!this.isDirty) {
			this.baselineDefaults = copyDefaults(next)
		} else {
			this.baselineDefaults = copyDefaults(next)
			this.isDirty = false
		}
		return true
	}

	seedCourseIfEmpty({ course }) {
		if (!course) return false
		if (!this._sourcesSettled()) return false
		if (!this._ensureEmptyDraftForSeed()) return false
		if (this.draftDefaults.course) return false

		const courseLayer = this.sourceByField?.course
		if (
			courseLayer === 'counselorStudentProfile' ||
			courseLayer === 'studentJourney' ||
			courseLayer === 'student' ||
			courseLayer === 'personal'
		) {
			return false
		}

		const next = {
			...this.draftDefaults,
			course,
			countries: Array.isArray(this.draftDefaults.countries)
				? [...this.draftDefaults.countries]
				: [],
		}
		this.draftDefaults = next
		if (!this.isDirty) {
			this.baselineDefaults = copyDefaults(next)
		} else {
			this.isDirty = !areDefaultsEqual(next, this.baselineDefaults)
		}
		return true
	}

	discardDraft() {
		this.draftDefaults = copyDefaults(this.baselineDefaults)
		this.isDirty = false
	}

	_clearStudentSources() {
		this.studentProfileSource = null
		this.studentSource = null
		this.onboardingSource = null
		this.sourcesStatus = 'idle'
		this.sourcesError = null
		this.resolvedDefaults = null
		this.sourceByField = null
		this.baselineDefaults = null
		this.draftDefaults = null
		this.isDirty = false
		this.saveStatus = 'idle'
		this.saveError = null
	}

	_abortStudentLoads() {
		this._studentAbortController?.abort()
		this._studentAbortController = null
	}

	_abortPersonalLoad() {
		this._personalAbortController?.abort()
		this._personalAbortController = null
		this._personalInflight = null
	}
}

export const researchSessionStore = new ResearchSessionStore()
